import { Modal, Radio } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type { AttachBlockVolumeRequest, BlockVolume } from '@xver/apis/v1/block-storage/model';
import { Button, InputField } from '@xver/components/Atoms';
import Select from '@xver/components/Atoms/Select/Select';
import { useInstanceListQuery } from '@xver/hooks/CloudComputeHooks/useInstanceListQuery';
import { useProductAccess } from '@xver/hooks/useActiveProject';
import { SELECT_SEARCHABLE_AFTER } from '@xver/utils/projects';
import { FormEvent, ReactNode, Ref, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useContainerListQuery } from '../../ContainerEnginePage/hooks/useContainerListQuery';
import {
    validateMountPath,
    VOLUME_ERROR_DEFAULTS,
} from '../../ContainerEngineInstancePage/utils/volumeValidation';
import {
    useBlockStorageErrorText,
    useBlockVolumeMutations,
} from '../hooks/useBlockVolumeMutations';
import { useBlockVolumesQuery } from '../hooks/useBlockVolumesQuery';

export type AttachTarget =
    | { type: 'vm'; instanceId: number; name: string }
    | { type: 'container'; containerId: string; name: string };

type TargetType = AttachTarget['type'];

/** kufer refuses to attach while a container is building or deploying. */
const ATTACHABLE_CONTAINER_STATUSES = new Set(['running', 'stopped', 'failed']);
const ATTACHABLE_VM_POWER = new Set(['running', 'stopped']);
/** Matches the container list's poll, so a VM that becomes attachable shows up while the dialog is open. */
const TARGET_REFETCH_MS = 5000;

interface AttachVolumeModalProps {
    opened: boolean;
    onClose: () => void;
    /** The volume to attach; omit to choose an available volume from `projectId`. */
    volume?: BlockVolume | null;
    /** Where to attach; omit to choose a VM or container in the active project. */
    target?: AttachTarget;
    /** Project to choose the volume from; defaults to the active project. */
    projectId?: string;
}

export default function AttachVolumeModal({
    opened,
    onClose,
    volume,
    target,
    projectId,
}: AttachVolumeModalProps) {
    const { t } = useTranslation(['blockStorage', 'common']);
    const { attachVolume } = useBlockVolumeMutations();
    const { reset, isPending } = attachVolume;

    useEffect(() => {
        if (opened) reset();
    }, [opened, reset]);

    const handleClose = () => {
        if (!isPending) onClose();
    };

    return (
        <Modal
            size="lg"
            opened={opened}
            onClose={handleClose}
            centered
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
            closeButtonProps={{ 'aria-label': t('common:actions.close'), disabled: isPending }}
            title={
                <span className="text-base font-bold">
                    {volume
                        ? t('attach.titleVolume', { name: volume.name })
                        : t('attach.titleTarget', { name: target?.name ?? '' })}
                </span>
            }
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
        >
            {opened && (
                <AttachForm
                    fixedVolume={volume ?? undefined}
                    fixedTarget={target}
                    projectId={projectId}
                    attachVolume={attachVolume}
                    onClose={handleClose}
                    onDone={onClose}
                />
            )}
        </Modal>
    );
}

interface AttachFormProps {
    fixedVolume?: BlockVolume;
    fixedTarget?: AttachTarget;
    projectId?: string;
    attachVolume: ReturnType<typeof useBlockVolumeMutations>['attachVolume'];
    onClose: () => void;
    onDone: () => void;
}

function AttachForm({
    fixedVolume,
    fixedTarget,
    projectId,
    attachVolume,
    onClose,
    onDone,
}: AttachFormProps) {
    const { t } = useTranslation(['blockStorage', 'containerEngineInstance', 'common']);
    const errorText = useBlockStorageErrorText();
    const placeholder = usePlaceholder();
    const canAttachVm = useProductAccess('vm') === 'manage';
    const canAttachContainer = useProductAccess('container') === 'manage';

    const allowedTypes: TargetType[] = fixedTarget
        ? [fixedTarget.type]
        : [
              ...(canAttachVm ? (['vm'] as const) : []),
              ...(canAttachContainer ? (['container'] as const) : []),
          ];

    const [chosenType, setType] = useState<TargetType>();
    const type = chosenType ?? allowedTypes[0];
    const [volumeId, setVolumeId] = useState('');
    const [instanceId, setInstanceId] = useState('');
    const [containerId, setContainerId] = useState('');
    const [mountPath, setMountPath] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const volumeRef = useRef<HTMLInputElement>(null);
    const vmRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLInputElement>(null);
    const mountPathRef = useRef<HTMLInputElement>(null);

    const volumesQuery = useBlockVolumesQuery({ projectId, enabled: !fixedVolume });
    const volumes = (volumesQuery.data?.data ?? []).filter(
        (candidate) => candidate.status === 'available' && candidate.access === 'manage'
    );
    const volume = fixedVolume ?? volumes.find((candidate) => candidate.id === volumeId);
    const isContainer = type === 'container';
    const pickVm = type === 'vm' && !fixedTarget;
    const pickContainer = isContainer && !fixedTarget;
    const mountPathError = isContainer ? validateMountPath(mountPath) : null;

    const errors = {
        volume: volume ? undefined : t('attach.errors.volumeRequired'),
        vm: pickVm && !instanceId ? t('attach.errors.vmRequired') : undefined,
        container: pickContainer && !containerId ? t('attach.errors.containerRequired') : undefined,
        mountPath: mountPathError
            ? t(
                  `containerEngineInstance:volumes.validation.${mountPathError}`,
                  VOLUME_ERROR_DEFAULTS[mountPathError]
              )
            : undefined,
    };
    const shown = (message: string | undefined) => (showErrors ? message : undefined);
    const autofocus = !fixedVolume
        ? 'volume'
        : !fixedTarget && allowedTypes.length > 1
          ? 'type'
          : pickVm
            ? 'vm'
            : pickContainer
              ? 'container'
              : 'mountPath';

    const request = (): AttachBlockVolumeRequest =>
        isContainer
            ? {
                  type: 'container',
                  containerId:
                      fixedTarget?.type === 'container' ? fixedTarget.containerId : containerId,
                  mountPath: mountPath.trim(),
              }
            : {
                  type: 'vm',
                  instanceId:
                      fixedTarget?.type === 'vm' ? fixedTarget.instanceId : Number(instanceId),
              };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (attachVolume.isPending) return;
        const invalidRef =
            (errors.volume && volumeRef) ||
            (errors.vm && vmRef) ||
            (errors.container && containerRef) ||
            (errors.mountPath && mountPathRef) ||
            null;
        if (invalidRef || !volume) {
            setShowErrors(true);
            invalidRef?.current?.focus();
            return;
        }
        attachVolume.mutate({ volume, request: request() }, { onSuccess: onDone });
    };

    if (!type) {
        return (
            <div className="flex flex-col gap-4">
                <p className="text-sm text-fg/78">{t('attach.noTargetAccess')}</p>
                <div className="flex justify-end">
                    <Button variant="secondary" onClick={onClose} data-autofocus>
                        {t('common:actions.close')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            {!fixedVolume && (
                <Select
                    ref={volumeRef}
                    data-autofocus
                    label={t('attach.volumeLabel')}
                    description={t('attach.volumeDescription')}
                    placeholder={placeholder(
                        volumesQuery.isPending,
                        volumes.length,
                        t('attach.noVolumes')
                    )}
                    data={volumes.map((candidate) => ({
                        value: candidate.id,
                        label: t('attach.volumeOption', {
                            name: candidate.name,
                            size: candidate.sizeGiB,
                        }),
                    }))}
                    value={volumeId || null}
                    onChange={(value) => setVolumeId(value ?? '')}
                    allowDeselect={false}
                    searchable={volumes.length > SELECT_SEARCHABLE_AFTER}
                    nothingFoundMessage={t('attach.nothingFound')}
                    error={shown(errors.volume)}
                    readOnly={attachVolume.isPending}
                    size="md"
                />
            )}

            {!fixedTarget &&
                (allowedTypes.length > 1 ? (
                    <Radio.Group
                        label={t('attach.typeLabel')}
                        value={type}
                        onChange={(value) => setType(value as TargetType)}
                    >
                        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:gap-6">
                            {allowedTypes.map((option) => (
                                <Radio
                                    key={option}
                                    value={option}
                                    label={t(`attach.types.${option}`)}
                                    size="sm"
                                    data-autofocus={
                                        (autofocus === 'type' && option === type) || undefined
                                    }
                                />
                            ))}
                        </div>
                    </Radio.Group>
                ) : (
                    <p className="text-sm text-fg/78">
                        {t('attach.onlyType', { type: t(`attach.types.${type}`) })}
                    </p>
                ))}

            {pickVm && (
                <VmSelect
                    selectRef={vmRef}
                    autoFocus={autofocus === 'vm'}
                    value={instanceId}
                    onChange={setInstanceId}
                    error={shown(errors.vm)}
                    readOnly={attachVolume.isPending}
                />
            )}

            {pickContainer && (
                <ContainerSelect
                    selectRef={containerRef}
                    autoFocus={autofocus === 'container'}
                    value={containerId}
                    onChange={setContainerId}
                    error={shown(errors.container)}
                    readOnly={attachVolume.isPending}
                />
            )}

            {isContainer && (
                <InputField
                    ref={mountPathRef}
                    data-autofocus={autofocus === 'mountPath' || undefined}
                    size="md"
                    width="100%"
                    label={t('attach.mountPathLabel')}
                    description={t('attach.mountPathDescription')}
                    placeholder="/data"
                    value={mountPath}
                    onChange={(event) => setMountPath(event.target.value)}
                    error={shown(errors.mountPath)}
                    readOnly={attachVolume.isPending}
                    autoComplete="off"
                    spellCheck={false}
                />
            )}

            <Note>{t('portability')}</Note>
            <Note>{t(isContainer ? 'attach.noteContainer' : 'attach.noteVm')}</Note>

            {attachVolume.error && (
                <p role="alert" className="text-sm text-fg">
                    {errorText(attachVolume.error)}
                </p>
            )}

            <div className="flex flex-wrap justify-end gap-2">
                <Button variant="secondary" onClick={onClose} disabled={attachVolume.isPending}>
                    {t('common:actions.cancel')}
                </Button>
                <Button type="submit" isLoading={attachVolume.isPending}>
                    {t('attach.submit')}
                </Button>
            </div>
        </form>
    );
}

function usePlaceholder() {
    const { t } = useTranslation('blockStorage');
    return (isLoading: boolean, count: number, empty: string) =>
        isLoading ? t('attach.loading') : count === 0 ? empty : t('attach.choose');
}

interface TargetSelectProps {
    selectRef: Ref<HTMLInputElement>;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    readOnly: boolean;
    /** Takes the dialog's initial focus. */
    autoFocus: boolean;
}

function VmSelect({ selectRef, value, onChange, error, readOnly, autoFocus }: TargetSelectProps) {
    const { t } = useTranslation('blockStorage');
    const placeholder = usePlaceholder();
    const { data, isPending } = useInstanceListQuery({
        staleTime: 0,
        refetchInterval: TARGET_REFETCH_MS,
    });
    const instances = (data ?? []).filter(
        (instance) => instance.access === 'manage' && ATTACHABLE_VM_POWER.has(instance.state.power)
    );

    return (
        <Select
            ref={selectRef}
            data-autofocus={autoFocus || undefined}
            label={t('attach.vmLabel')}
            description={t('attach.vmDescription')}
            placeholder={placeholder(isPending, instances.length, t('attach.noVms'))}
            data={instances.map((instance) => ({
                value: String(instance.id),
                label: `${instance.hostname} (${instance.network.ipAddress})`,
            }))}
            value={value || null}
            onChange={(next) => onChange(next ?? '')}
            allowDeselect={false}
            searchable={instances.length > SELECT_SEARCHABLE_AFTER}
            nothingFoundMessage={t('attach.nothingFound')}
            error={error}
            readOnly={readOnly}
            size="md"
        />
    );
}

function ContainerSelect({
    selectRef,
    value,
    onChange,
    error,
    readOnly,
    autoFocus,
}: TargetSelectProps) {
    const { t } = useTranslation('blockStorage');
    const placeholder = usePlaceholder();
    const { data, isPending } = useContainerListQuery();
    const containers = (data?.containers ?? []).filter((container) =>
        ATTACHABLE_CONTAINER_STATUSES.has(container.status)
    );

    return (
        <Select
            ref={selectRef}
            data-autofocus={autoFocus || undefined}
            label={t('attach.containerLabel')}
            description={t('attach.containerDescription')}
            placeholder={placeholder(isPending, containers.length, t('attach.noContainers'))}
            data={containers.map((container) => ({ value: container.id, label: container.name }))}
            value={value || null}
            onChange={(next) => onChange(next ?? '')}
            allowDeselect={false}
            searchable={containers.length > SELECT_SEARCHABLE_AFTER}
            nothingFoundMessage={t('attach.nothingFound')}
            error={error}
            readOnly={readOnly}
            size="md"
        />
    );
}

function Note({ children }: { children: ReactNode }) {
    return (
        <div className="flex flex-row items-start gap-2 p-3 rounded-md border-[0.5px] border-line bg-fg/5">
            <IconInfoCircle size={16} className="shrink-0 mt-0.5 text-fg/58" aria-hidden="true" />
            <p className="text-sm text-fg/78">{children}</p>
        </div>
    );
}
